import { logoutToLogin } from "services/utils/sessionLifecycle";
import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import packageJson from "../../../package.json";
import {
  ArrowBack,
  ChevronRight,
  Person2,
  Dns,
  LockReset,
  Task,
  LogoutRounded,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import { supportLabel } from "services/constants";
import { ProfileEditView } from "./ProfileEditView";
import { ChangePassword } from "pages/GateKeeper/ChangePassword";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import {
  designColors,
  designFontFamily,
  designWeights,
  designRadii,
  designShell,
  withDesignFont,
} from "services/constants/designTokens";
import { setCurrentLocation } from "services/Redux/userToken";
import {
  setCurrentScreen,
  setSubMenuExpand,
} from "services/Redux/userManagament";

import { useSelector, useDispatch } from "react-redux";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { customGetAPI } from "services/utils/CustomAPI";
import { useHistory, useLocation } from "react-router-dom";
import { resetNotifications } from "services/Redux/notificationReducer";

const dangerButtonSx = {
  ...designShell.panelDangerButton,
  color: "#B42318",
  borderColor: "#F5C2C7",
  backgroundColor: "#FEF3F2",
  "&:hover": {
    borderColor: "#E59CA5",
    backgroundColor: "#FEE4E2",
  },
};

export const UserSidePeek = ({
  openSidePeek,
  setOpenSidePeek,
  firstLetter,
  userDetails,
  updateProfileSidePeek,
  setUpdateProfileSidePeek,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { productAccess, domain } = useSelector(
    (state) => state.tokenReducer,
  );
  const [actionData, setActionData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [teantCount, setTenatCount] = useState(0);
  const [panelView, setView] = useState("profile");
  // The no-product-access route uses this same panel as its page. Opening
  // the avatar menu still provides Change Password and Logout; closing it
  // returns to the expanded profile instead of leaving an empty page.
  const isProfileLanding =
    location.pathname.replace(/\/$/, "") === "/admin/edit/profile" &&
    !openSidePeek;
  const view = isProfileLanding ? "edit-profile" : panelView;
  // Controlled (not left to GlobalSidePanel's own internal state) purely so
  // it can be reset to collapsed in handleClosePanel — every drill-down view
  // opens in the normal drawer; expanding to full view is only ever the
  // user's own choice via the header's "Open full view" toggle.
  const [expanded, setExpanded] = useState(false);
  // Drives the sticky footer for the "change-password" drill-down view —
  // that form lives in its own component, so its save state is lifted here
  // to render outside the scrollable body (same split as the Approval
  // Center create screens: header/footer pinned, only the body scrolls).
  // "edit-profile" (My Profile) has no footer — it's a read-only view now.
  const [passwordState, setPasswordState] = useState({
    buttonValidation: true,
    buttonLoader: false,
    submit: () => {},
  });

  useEffect(() => {
    const getTenantDetails = async () => {
      const url = userDetails?.email
        ? `${domain}user_tenant/details/?email=${userDetails?.email}`
        : `${domain}user_tenant/details/?phone_number=${userDetails?.phone_number}`;
      const res = await customGetAPI(url);
      if (res.ok) {
        setTenatCount(res?.responseDetails?.length);
      }
    };
    if (userDetails && userDetails?.type === supportLabel) {
      getTenantDetails();
    }
  }, [domain, userDetails]);

  useEffect(() => {
    if (updateProfileSidePeek) {
      const hasProject = productAccess?.some(
        (product) => product.product_name === "Projects"
      );
      let actionDetails = [
        { id: 1, label: "My Profile", icon: <Person2 fontSize="small" /> },
        {
          id: 4,
          label: "Change Password",
          icon: <LockReset fontSize="small" />,
        },
      ];
      if (teantCount > 1) {
        actionDetails?.splice(1, 0, {
          id: 2,
          label: "Change Domain",
          icon: <Dns fontSize="small" />,
        });
      }
      if (hasProject) {
        actionDetails?.splice(teantCount > 1 ? 2 : 1, 0, {
          id: 3,
          label: "Task",
          icon: <Task fontSize="small" />,
        });
      }
      setActionData(actionDetails);
      setUpdateProfileSidePeek(false);
    }
  }, [
    actionData,
    productAccess,
    setUpdateProfileSidePeek,
    teantCount,
    updateProfileSidePeek,
  ]);

  const handleCloseDialogBox = () => {
    setOpenDialog(false);
  };

  const handleClosePanel = () => {
    setOpenSidePeek(false);
    setView("profile");
    setExpanded(false);
  };

  const logoutCurrentUser = () => {
    dispatch(resetNotifications());
    logoutToLogin();
  };

  const buttonAction = (item) => {
    if (item.label === "My Profile") {
      // The shared profile view loads the signed-in user's own details.
      dispatch(setGlobalSearchValue(""));
      setView("edit-profile");
    } else if (item.label === "Change Password") {
      dispatch(setGlobalSearchValue(""));
      setView("change-password");
    } else if (item.label === "Change Domain") {
      dispatch(setGlobalSearchValue(""));
      history.push("/admin/change/domain");
      setOpenSidePeek(false);
    } else if (item.label === "Task") {
      dispatch(setGlobalSearchValue(""));
      dispatch(setCurrentLocation("Projects"));
      localStorage.setItem("currentScreen", "Tasks");
      dispatch(setCurrentScreen("Tasks"));
      dispatch(setSubMenuExpand(false));
      history.push("/admin/project/mytask");
      setOpenSidePeek(false);
    }
  };

  const renderDrillHeader = (title) => (
    <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <Mui.IconButton
        onClick={() => {
          setView("profile");
          setExpanded(false);
        }}
        size="small"
        sx={{ ...designShell.panelSoftIconButton, color: designColors.slate500 }}
      >
        <ArrowBack fontSize="small" />
      </Mui.IconButton>
      <Mui.Typography
        sx={{
          fontSize: 18,
          fontWeight: designWeights.heading,
          fontFamily: designFontFamily,
          color: designColors.slate800,
        }}
      >
        {title}
      </Mui.Typography>
    </Mui.Stack>
  );

  const logoutFooter = (
    <Mui.Stack spacing={1}>
      <Mui.Typography
        sx={{
          textAlign: "center",
          fontSize: 12.5,
          fontFamily: designFontFamily,
          color: "text.secondary",
        }}
      >
        Version {packageJson.version}
      </Mui.Typography>
    <Mui.Button
      fullWidth
      variant="outlined"
      startIcon={<LogoutRounded fontSize="small" />}
      onClick={() => {
        setOpenDialog(true);
        setOpenSidePeek(false);
      }}
      sx={dangerButtonSx}
    >
      Log out
    </Mui.Button>
    </Mui.Stack>
  );

  const changePasswordFooter = (
    <PanelActionFooter
      onCancel={() => setView("profile")}
      primaryLabel="Set Password"
      primaryProps={{
        onClick: passwordState.submit,
        loading: passwordState.buttonLoader,
        disabled: passwordState.buttonValidation || passwordState.buttonLoader,
      }}
    />
  );

  // UserSidePeek is mounted in Sidebar/index.js's AppBar, which persists
  // across every route and isn't a descendant of any screen's own
  // <ThemeProvider theme={designTheme}> — so GlobalSidePanel's own title
  // text (and the logout confirmation dialog below) would fall back to
  // whichever font the ambient theme happens to be instead of Poppins.
  // withDesignFont layers Poppins on top while keeping this tree's palette
  // (the confirmation-dialog styling, etc.) intact — same fix as
  // NotificationBell.js's panel.
  return (
    <ThemeProvider theme={withDesignFont}>
      {/* No width override — the shared GlobalSidePanel default
          (PANEL_DRAWER_WIDTH, 620) is the same width every other detail
          panel in the app uses (Approve's Apply.js screens, the
          notification bell panel, etc). */}
      <GlobalSidePanel
        open={openSidePeek || isProfileLanding}
        onClose={handleClosePanel}
        expanded={isProfileLanding || expanded}
        onToggleExpand={() => setExpanded((prev) => !prev)}
        hideClose={isProfileLanding}
        hideExpand={isProfileLanding}
        title={isProfileLanding || view === "profile" ? "My Profile" : undefined}
        header={
          isProfileLanding
            ? undefined
            : view === "edit-profile"
              ? renderDrillHeader("My Profile")
              : view === "change-password"
                ? renderDrillHeader("Change Password")
                : undefined
        }
        footer={
          view === "profile"
            ? logoutFooter
            : view === "change-password"
              ? changePasswordFooter
              : undefined
        }
        bodySx={{ p: 0 }}
      >
        {view === "edit-profile" ? (
          <ProfileEditView />
        ) : view === "change-password" ? (
          <ChangePassword onStateChange={setPasswordState} hideInlineButton />
        ) : (
          <Mui.Box>
            <Mui.Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.4,
                px: 3,
                py: 3,
                borderBottom: `1px solid ${designColors.slate200}`,
              }}
            >
              <Mui.Avatar
                alt={firstLetter}
                src={userDetails?.image_url}
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: designColors.tealDark,
                  fontSize: 26,
                  fontFamily: designFontFamily,
                }}
              >
                {firstLetter}
              </Mui.Avatar>
              <Mui.Typography
                sx={{
                  mt: 1,
                  fontSize: 18,
                  fontWeight: designWeights.heading,
                  fontFamily: designFontFamily,
                  color: designColors.slate900,
                }}
              >
                {userDetails?.first_name}
              </Mui.Typography>
              <Mui.Typography
                sx={{ fontSize: 13, fontFamily: designFontFamily, color: designColors.slate600 }}
              >
                {userDetails?.email}
              </Mui.Typography>
              <Mui.Typography
                sx={{ fontSize: 13, fontFamily: designFontFamily, color: designColors.slate500 }}
              >
                {[userDetails?.employee_id, userDetails?.role].filter(Boolean).join(" • ")}
              </Mui.Typography>
            </Mui.Box>

            <Mui.Box sx={{ p: 1.5 }}>
              {actionData.map((item) => (
                <Mui.ButtonBase
                  key={item.id}
                  onClick={() => buttonAction(item)}
                  sx={{
                    width: "100%",
                    justifyContent: "space-between",
                    textAlign: "left",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: designRadii.control,
                    "&:hover": { backgroundColor: designColors.slate100 },
                  }}
                >
                  <Mui.Stack direction="row" spacing={1.5} alignItems="center">
                    <Mui.Box sx={{ color: designColors.slate500, display: "flex" }}>
                      {item.icon}
                    </Mui.Box>
                    <Mui.Typography
                      sx={{
                        fontSize: 14.5,
                        fontWeight: designWeights.medium,
                        fontFamily: designFontFamily,
                        color: designColors.slate800,
                      }}
                    >
                      {item.label}
                    </Mui.Typography>
                  </Mui.Stack>
                  <ChevronRight sx={{ fontSize: 18, color: designColors.slate400 }} />
                </Mui.ButtonBase>
              ))}
            </Mui.Box>
          </Mui.Box>
        )}
      </GlobalSidePanel>
      <GlobalConfirmDialog
        open={openDialog}
        title="Log out"
        onCancel={handleCloseDialogBox}
        onConfirm={logoutCurrentUser}
        confirmLabel="Log out"
        cancelLabel="Cancel"
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
          Are you sure you want to log out?
        </Mui.Typography>
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
